/**
 * Learning Moments → Taxonomy → one node's evidence.
 *
 * A full page rather than an expander because this is where the actual judgement
 * happens: deciding whether a cluster is a real learning moment means reading the
 * student work behind it, not skimming a label. That needs room — the source
 * sentence, what it normalised to, and a way through to the solution analysis it
 * came from.
 *
 * Read-only. Every row links to /submissions/:id, which is the existing
 * SolutionAnalysisDetail view — this page adds no analysis of its own.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useSubjectLabel } from '@shared-ui/reference-data';
import { BackButton } from '@shared-ui/components';
import { fetchWithAdmin } from '../../api/fetchHelper';
import './InsightNodeDrill.css';

interface Exemplar { text: string; count: number }

interface Item {
  itemId: string;
  submissionId?: string | null;
  questionId?: string | null;
  subject?: string | null;
  topic?: string | null;
  stepNumber?: number | null;
  rawText: string;
  normalised?: string | null;
  cascade?: boolean;
  noError?: boolean;
  confidence?: string;
}

interface Node {
  key: string;
  label: string;
  description: string;
  scope: string | null;
  itemCount: number;
  distinctPhrases: number;
  subject?: string | null;
  topic?: string;
  parentLabel?: string;
  exemplars: Exemplar[];
  echoesParent?: boolean;
}

const PAGE_SIZE = 25;

export function InsightNodeDrill() {
  const { runId = '', kind = 'child', nodeKey = '' } = useParams();
  const [page, setPage] = useState(1);
  const subjectLabel = useSubjectLabel();

  const tax = useQuery({
    // Same key the taxonomy page uses, so arriving from it costs no extra fetch.
    queryKey: ['insight-taxonomy', runId],
    queryFn: () => fetchWithAdmin<{ parents: Node[]; children: Node[] }>(
      `/console/insight-runs/${runId}/taxonomy`,
    ),
    enabled: !!runId,
  });

  const node = (kind === 'parent' ? tax.data?.parents : tax.data?.children)
    ?.find(n => n.key === nodeKey);

  const filterParam = kind === 'parent' ? 'parentKey' : 'childKey';
  const items = useQuery({
    queryKey: ['insight-node-items', runId, kind, nodeKey, page],
    queryFn: () => fetchWithAdmin<{ items: Item[]; total: number }>(
      `/console/insight-runs/${runId}/items`
      + `?${filterParam}=${encodeURIComponent(nodeKey)}&page=${page}&pageSize=${PAGE_SIZE}`,
    ),
    enabled: !!runId && !!nodeKey,
    placeholderData: keepPreviousData,
  });

  const total = items.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="nd-page">
      {/* Pops history, so the taxonomy comes back with its open family intact;
          the fallback only matters on a deep link or refresh. */}
      <BackButton fallback="/learning-moments/taxonomy" label="Taxonomy" />

      {node && (
        <header className="nd-head">
          <div className="nd-crumb">
            {kind === 'child' ? (
              <>
                <span className="nd-crumb-parent">{node.parentLabel}</span>
                <span className="nd-crumb-sep">›</span>
                <span>{node.subject ? subjectLabel(node.subject) : ''} {node.topic}</span>
              </>
            ) : (
              <span>Family · {node.scope ?? 'unscoped'}</span>
            )}
          </div>

          <h1 className="nd-title">{node.label}</h1>
          <p className="nd-desc">{node.description}</p>

          <div className="nd-facts">
            <div className="nd-fact">
              <span className="nd-fact-n">{node.itemCount.toLocaleString()}</span>
              <span className="nd-fact-l">items</span>
            </div>
            <div className="nd-fact">
              <span className="nd-fact-n">{node.distinctPhrases.toLocaleString()}</span>
              <span className="nd-fact-l">distinct phrases</span>
            </div>
            {node.echoesParent && (
              <div className="nd-flag">Restates its parent — prune candidate</div>
            )}
          </div>

          {node.exemplars.length > 0 && (
            <section className="nd-block">
              <h2 className="nd-h2">Phrases that defined this cluster</h2>
              <ul className="nd-phrases">
                {node.exemplars.map((e, i) => (
                  <li key={i}>
                    <span className="nd-count">{e.count}×</span>
                    <span>{e.text}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </header>
      )}

      <section className="nd-block">
        <div className="nd-evhead">
          <h2 className="nd-h2">Evidence</h2>
          {total > 0 && (
            <span className="nd-evcount">
              {total.toLocaleString()} {total === 1 ? 'item' : 'items'}
            </span>
          )}
        </div>
        <p className="nd-hint">
          Each row is one sentence the analyser wrote about a student&rsquo;s step. Open the
          solution to see it in context before deciding this cluster is real.
        </p>

        {items.isLoading && <p className="nd-muted">Loading evidence…</p>}
        {items.isError && <p className="nd-error">Failed: {(items.error as Error).message}</p>}
        {items.data && items.data.items.length === 0 && (
          <p className="nd-muted">
            No items carry this cluster. Runs published before drill-down existed have no
            assignment stamped — republish the run to populate it.
          </p>
        )}

        <ol className="nd-items">
          {items.data?.items.map(it => (
            <li key={it.itemId} className="nd-item">
              <div className="nd-item-main">
                {it.normalised && <p className="nd-norm">{it.normalised}</p>}
                <p className="nd-raw">{it.rawText}</p>
              </div>
              <div className="nd-item-side">
                <div className="nd-meta">
                  {it.subject && <span>{subjectLabel(it.subject)}</span>}
                  {it.topic && <span className="nd-meta-topic">{it.topic}</span>}
                  {it.stepNumber != null && <span>Step {it.stepNumber}</span>}
                </div>
                <div className="nd-tags">
                  {it.cascade && <span className="nd-tag" title="Knock-on from an earlier error">cascade</span>}
                  {it.noError && <span className="nd-tag" title="Model found nothing wrong">no error</span>}
                  {it.confidence && it.confidence !== 'high' && (
                    <span className="nd-tag nd-tag-warn">{it.confidence}</span>
                  )}
                </div>
                {it.submissionId && (
                  <Link className="nd-link" to={`/submissions/${it.submissionId}`}>
                    Open solution →
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ol>

        {pages > 1 && (
          <div className="nd-pager">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</button>
            <span>Page {page} of {pages}</span>
            <button disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        )}
      </section>
    </div>
  );
}
